import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { CheckCircle, XCircle, Clock, ArrowRight, Sparkle } from "@phosphor-icons/react";

const ALL_PROGRAMS = ["journey90", "grief180", "sober30", "chronic365"];

// Where to send the user based on what they just bought
const JOURNAL_ROUTE = {
  journey90: "/app",
  grief180: "/app/grief",
  sober30: "/app/sober",
  chronic365: "/app/chronic",
};

const UPSELL = {
  // missing set => { pkg, label, price, originalPrice }
  "grief180,sober30": { pkg: "upsell_add_grief_sober", label: "Add Grief + Sober Living", price: 40, original: 55.98 },
  "journey90,sober30": { pkg: "upsell_add_journey_sober", label: "Add Rediscover + Sober Living", price: 40, original: 45.98 },
  "journey90,grief180": { pkg: "upsell_add_journey_grief", label: "Add Rediscover + Grief", price: 50, original: 69.98 },
  "journey90": { pkg: "upsell_add_journey", label: "Add Rediscover Yourself", price: 19.99, original: 29.99 },
  "grief180": { pkg: "upsell_add_grief", label: "Add Moving through Grief", price: 29.99, original: 39.99 },
  "sober30": { pkg: "upsell_add_sober", label: "Add Sober Living", price: 9.99, original: 15.99 },
};

export default function PaymentResult() {
  const [params] = useSearchParams();
  const sessionId = params.get("tx") || params.get("session_id");
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState("polling");
  const [purchased, setPurchased] = useState(null); // package_id + programs from tx
  const [upsellLoading, setUpsellLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPurchased({ package_id: data.package_id, programs: data.programs || [] });
          setStatus("paid"); await refresh(); return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (attempts >= 8) { setStatus("failed"); return; }
        setTimeout(poll, 2500);
      } catch {
        if (attempts >= 8) setStatus("failed"); else setTimeout(poll, 2500);
      }
    };
    poll();
  }, [sessionId, refresh]);

  const startUpsell = async (pkg) => {
    setUpsellLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin, package_id: pkg });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Checkout failed.");
      setUpsellLoading(false);
    }
  };

  const missing = ALL_PROGRAMS.filter((p) => !(user?.programs || []).includes(p));
  const upsell = missing.length > 0 ? UPSELL[missing.join(",")] : null;

  return (
    <div className="max-w-2xl mx-auto py-10 space-y-6">
      <div className="bg-white rounded-3xl border border-border p-10 text-center soft-shadow">
        {status === "polling" && (<>
          <Clock size={48} weight="duotone" className="mx-auto text-sage mb-4 animate-breathe" />
          <h1 className="font-serif text-3xl mb-2">Confirming your payment…</h1>
          <p className="text-muted-foreground">This usually takes a few seconds.</p>
        </>)}
        {status === "paid" && (<>
          <CheckCircle size={48} weight="duotone" className="mx-auto text-moss mb-4" />
          <h1 className="font-serif text-3xl mb-2">Welcome.</h1>
          <p className="text-muted-foreground mb-6">Your journey is unlocked, for life.</p>
          <Link to={JOURNAL_ROUTE[purchased?.programs?.[0]] || JOURNAL_ROUTE[purchased?.package_id] || "/app"} data-testid="payment-back-to-app"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-moss text-white hover:bg-mossDark">
            Continue to your journal <ArrowRight size={16} />
          </Link>
        </>)}
        {(status === "failed" || status === "expired") && (<>
          <XCircle size={48} weight="duotone" className="mx-auto text-destructive mb-4" />
          <h1 className="font-serif text-3xl mb-2">Payment not completed.</h1>
          <p className="text-muted-foreground mb-6">No charge was made. You can try again whenever you're ready.</p>
          <Link to="/app/pricing" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-moss text-white hover:bg-mossDark">
            Back to pricing <ArrowRight size={16} />
          </Link>
        </>)}
      </div>

      {status === "paid" && upsell && (
        <div className="relative bg-charcoal text-white rounded-3xl p-8 soft-shadow-lg overflow-hidden" data-testid="upsell-card">
          <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full bg-clay/30 blur-3xl" />
          <div className="relative">
            <p className="text-xs tracking-[0.3em] uppercase text-clay font-bold mb-3 flex items-center gap-2">
              <Sparkle size={14} weight="duotone"/> One-time offer · {missing.length === 1 ? "complete your collection" : "deepen the journey"}
            </p>
            <h3 className="font-serif text-3xl mb-3">{upsell.label}</h3>
            <p className="text-white/70 mb-5">
              For just a little more, add the {missing.length === 1 ? "remaining journey" : "remaining two journeys"} —
              save <span className="font-medium text-white">${(upsell.original - upsell.price).toFixed(2)}</span> when you do it now.
            </p>
            <div className="flex items-baseline gap-3 mb-6">
              <span className="font-serif text-4xl">${upsell.price.toFixed(2)}</span>
              <span className="text-white/50 line-through text-sm">${upsell.original.toFixed(2)}</span>
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => startUpsell(upsell.pkg)} disabled={upsellLoading} data-testid="upsell-accept"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-clay hover:bg-clay/90 disabled:opacity-60 transition-all">
                {upsellLoading ? "Opening…" : "Yes, add it"} <ArrowRight size={16}/>
              </button>
              <button onClick={() => navigate(JOURNAL_ROUTE[purchased?.programs?.[0]] || JOURNAL_ROUTE[purchased?.package_id] || "/app")} data-testid="upsell-decline"
                      className="px-6 py-3 rounded-full border border-white/20 hover:bg-white/10 text-sm transition-all">
                No thanks, continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
